import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'

function WellsDownPage() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [tab, setTab] = useState('down')

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        setLoading(true)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/workovers/wells-down`)
            if (!res.ok) throw new Error('Failed to fetch wells down data')
            setData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    const summary = data?.summary || {}
    const downWells = data?.wells || []
    const inactiveWells = data?.inactive || []
    const wells = tab === 'down' ? downWells : inactiveWells

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={`border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <button onClick={() => navigate('/workovers')}
                    className={`text-sm mb-2 inline-block ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                    &larr; Back to Workovers
                </button>
                <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    Wells Down
                </h1>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <SummaryCard theme={theme} label="Wells Down" value={summary.wellsDown || 0} />
                <SummaryCard theme={theme} label="Inactive / Shut-In" value={summary.wellsInactive || 0} />
                <SummaryCard theme={theme} label="Deferred Revenue" value={`$${(summary.totalDeferredRevenue || 0).toLocaleString()}`} />
                <SummaryCard theme={theme} label="WTI Price" value={`$${summary.wtiPrice?.toFixed(2) || '--'}/BBL`} />
            </div>

            {/* Tab Toggle */}
            <div className={`flex border-b ${theme === 'dark' ? 'border-slate-800' : 'border-gray-200'}`}>
                <button
                    onClick={() => setTab('down')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                        tab === 'down'
                            ? theme === 'dark'
                                ? 'text-white border-b-2 border-blue-500'
                                : 'text-gray-900 border-b-2 border-blue-600'
                            : theme === 'dark'
                                ? 'text-gray-400 hover:text-gray-200'
                                : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Down ({downWells.length})
                </button>
                <button
                    onClick={() => setTab('inactive')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                        tab === 'inactive'
                            ? theme === 'dark'
                                ? 'text-white border-b-2 border-blue-500'
                                : 'text-gray-900 border-b-2 border-blue-600'
                            : theme === 'dark'
                                ? 'text-gray-400 hover:text-gray-200'
                                : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Inactive / Shut-In ({inactiveWells.length})
                </button>
            </div>

            {/* Wells Table */}
            <div className="chart-container overflow-x-auto">
                {wells.length > 0 ? (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Well</th>
                                <th>{tab === 'down' ? 'Failure Type' : 'Status'}</th>
                                <th>Detail</th>
                                <th>Date Down</th>
                                <th className="text-right">Days</th>
                                <th className="text-right">Avg BOPD</th>
                                <th className="text-right">Avg Gas</th>
                                <th className="text-right">Deferred Oil</th>
                                <th className="text-right">Revenue Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            {wells.map((w, i) => (
                                <tr key={w.downtimeId || i}
                                    className={w.wellId ? 'cursor-pointer' : ''}
                                    onClick={() => w.wellId && navigate(`/wells/${w.wellId}`)}>
                                    <td className="font-medium">
                                        {w.wellName}
                                        {w.wellId && <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>&rarr;</span>}
                                    </td>
                                    <td>{w.downtimeCode || '--'}</td>
                                    <td className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>{w.downtimeCode2 || '--'}</td>
                                    <td>{w.dateDown ? new Date(w.dateDown).toLocaleDateString() : '--'}</td>
                                    <td className="text-right">
                                        <span className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${
                                            w.daysDown > 14
                                                ? theme === 'dark' ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                                                : w.daysDown > 7
                                                ? theme === 'dark' ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                                                : theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                                        }`}>
                                            {w.daysDown}d
                                        </span>
                                    </td>
                                    <td className="text-right font-mono">{w.avgBopd}</td>
                                    <td className="text-right font-mono">{w.avgGas}</td>
                                    <td className="text-right font-mono">{w.deferredOil?.toLocaleString()}</td>
                                    <td className="text-right font-mono text-red-400">${w.revenueImpact?.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className={`text-center py-8 ${tab === 'down' ? 'text-green-400' : theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {tab === 'down' ? 'All wells operational' : 'No inactive wells'}
                    </p>
                )}
            </div>
        </div>
    )
}

function SummaryCard({ theme, label, value }) {
    return (
        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-gray-200'}`}>
            <p className={`text-[10px] sm:text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
            <p className={`text-lg sm:text-2xl font-bold mt-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{value}</p>
        </div>
    )
}

export default WellsDownPage
